import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeUpAnimation from '../../components/base/FadeUpAnimation';
import { useSEO, getSiteUrl } from '../../utils/seo';

const ProjectsPage = () => {
  useSEO({
    title: 'プロジェクト実績 — 小さな波紋が、静かに、広がっていく | 2b LLC',
    description: '2b LLCのプロジェクト実績。臼杵の港町カフェプロジェクト、政治・政策支援、AI業務改善コンサルティング、企業ブランディング支援など、誰かの想いが動いた瞬間の記録をご紹介します。',
    keywords: 'プロジェクト,実績,地域活性化,政策支援,AIコンサルティング,ブランディング,2b LLC',
    canonical: getSiteUrl('/projects'),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'プロジェクト実績',
      description: '2b LLCが手がけたプロジェクトの実績紹介',
      url: getSiteUrl('/projects'),
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'Project',
              name: '臼杵の港町カフェプロジェクト',
              description: '大分県臼杵市の港近くの物件を、地域の人と旅人がつながる港の縁側へと再生中'
            }
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'Project',
              name: '政治・政策支援プロジェクト',
              description: '地方議員や政治家の活動を、裏方として支援'
            }
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'Project',
              name: 'AI業務改善コンサルティング',
              description: '生成AIを共に考えるパートナーとして活用する支援'
            }
          },
          {
            '@type': 'ListItem',
            position: 4,
            item: {
              '@type': 'Project',
              name: '企業ブランディング支援',
              description: '企業の理念や採用メッセージを共感される形に'
            }
          }
        ]
      }
    }
  });

  return (
    <div className="min-h-screen bg-[#F6F3EB]">
      <Header />
      
      <main className="pt-32 pb-20">
        {/* ロゴセクション */}
        <section 
          className="py-12 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage: 'url(https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/e2213d932679368b2c76646fe04e2a17.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/75"></div>
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <FadeUpAnimation duration={1400}>
                <img 
                  src="https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/191bb64e22d504c48b73a6bcbfddf67c.png"
                  alt="2b LLC"
                  className="w-[480px] h-auto mx-auto mb-12 object-contain"
                />
              </FadeUpAnimation>
            </div>
            
            {/* ヒーローセクション */}
            <section 
              className="py-32 bg-[#F6F3EB] relative overflow-hidden"
              style={{
                backgroundImage: 'url(https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/e2213d932679368b2c76646fe04e2a17.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              <div className="absolute inset-0 bg-[#F6F3EB]/75"></div>
              <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
                <div className="text-center mb-32">
                  <FadeUpAnimation duration={1400}>
                    <h1 className="text-5xl md:text-6xl font-semibold text-[#4A4A48] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Projects
                    </h1>
                  </FadeUpAnimation>
                  
                  <FadeUpAnimation duration={1400}>
                    <p className="text-2xl md:text-3xl text-[#4A4A48] opacity-70" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      — 小さな波紋が、静かに、広がっていく。
                    </p>
                  </FadeUpAnimation>
                </div>
              </div>
            </section>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-6">
          {/* ---------- 対象文 ---------- */}
          <section className="mb-32">
            <FadeUpAnimation delay={150} duration={1400}>
              <div className="text-center mb-32 max-w-4xl mx-auto">
                <div className="space-y-6 text-[#4A4A48] leading-[1.8]" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                  <p className="text-sm md:text-xl">ここに載せるのは「誰かの想いが動いた瞬間」の記録。</p>
                  <div className="space-y-2">
                    <p className="text-sm md:text-xl">大きな成功ではなく、小さな変化。</p>
                    <p className="text-sm md:text-xl">派手な演出ではなく、確かな成果。</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm md:text-xl">2bは言葉と仕組みで "きっかけ" を作ってきました。</p>
                    <p className="text-sm md:text-xl">その一部をご紹介します。</p>
                  </div>
                </div>
              </div>
            </FadeUpAnimation>
          </section>

          {/* ---------- プロジェクト一覧 ---------- */}
          <section className="space-y-32">
            {/* 政治・政策支援プロジェクト */}
            <FadeUpAnimation delay={300} duration={1400}>
              <div className="mb-32">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  <div className="lg:order-2">
                    <img
                      alt="政治・政策支援プロジェクト"
                      className="w-full h-64 md:h-80 object-cover object-top rounded-xl shadow-sm"
                      src="https://public.readdy.ai/ai/img_res/a3d4408e0d0e1fdaffe0bee5ea81a4b0.jpg"
                    />
                  </div>
                  <div className="space-y-6 lg:order-1">
                    <h3 className="text-2xl md:text-3xl font-semibold text-[#4A4A48]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      政治の現場に、静かな支え。
                    </h3>
                    <p className="text-base md:text-xl text-[#4A4A48] leading-[1.75]" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      地方議員や政治家の活動を、裏方として支援しています。<br /><br />
                      政策立案、広報戦略、地域との対話設計など、<br />
                      目立たないけれど確かに必要な仕事を担います。<br />
                      政治の現場に、静かな推進力を届けます。
                    </p>
                    <ul className="space-y-2 text-base md:text-xl text-[#4A4A48]" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>政策立案支援</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>広報戦略設計</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>地域対話ファシリテーション</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>秘書業務代行</span>
                      </li>
                    </ul>
                    <p className="text-base md:text-xl text-[#4A4A48] italic opacity-80" style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}>
                      声にならない想いを、形にする。
                    </p>
                  </div>
                </div>
              </div>
            </FadeUpAnimation>

            {/* AI業務改善コンサルティング */}
            <FadeUpAnimation delay={450} duration={1400}>
              <div className="mb-32">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  <div>
                    <img
                      alt="AI業務改善コンサルティング"
                      className="w-full h-64 md:h-80 object-cover object-top rounded-xl shadow-sm"
                      src="https://readdy.ai/api/search-image?query=modern%20bright%20office%20desk%20with%20AI%20interface%20on%20computer%20screen%2C%20hands%20typing%2C%20clean%20workspace%2C%20natural%20lighting%2C%20productivity%20tools%2C%20technology%20consulting%20atmosphere%2C%20natural%20beige%20tones%2C%20desaturated%20calm%20lighting&width=600&height=400&seq=project-ai-consulting&orientation=landscape"
                    />
                  </div>
                  <div className="space-y-6">
                    <h3 className="text-2xl md:text-3xl font-semibold text-[#4A4A48]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      AIと共に、未来を描く。
                    </h3>
                    <p className="text-base md:text-xl text-[#4A4A48] leading-[1.75]" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      生成AIを「使いこなす」のではなく、<br />
                      「共に考えるパートナー」として活用する支援を行っています。<br /><br />
                      企業や自治体の業務改善、新規事業開発、<br />
                      人材育成まで、AIと人が協働する未来を設計します。
                    </p>
                    <ul className="space-y-2 text-base md:text-xl text-[#4A4A48]" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>生成AI導入コンサルティング</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>業務プロセス改善支援</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>AI活用研修・ワークショップ</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                        <span>新規事業開発支援</span>
                      </li>
                    </ul>
                    <p className="text-base md:text-xl text-[#4A4A48] italic opacity-80" style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}>
                      人間らしさを、拡張する。
                    </p>
                  </div>
                </div>
              </div>
            </FadeUpAnimation>

            {/* 企業ブランディング支援 */}
            <FadeUpAnimation delay={600} duration={1400}>
              <div className="mb-32">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  <div className="lg:order-2">
                    <img
                      alt="企業ブランディング支援"
                      className="w-full h-64 md:h-80 object-cover object-top rounded-xl shadow-sm"
                      src="https://readdy.ai/api/search-image?query=hands%20writing%20and%20editing%20corporate%20documents%20by%20window%2C%20warm%20natural%20light%2C%20brand%20strategy%20papers%2C%20thoughtful%20copywriting%20process%2C%20professional%20workspace%2C%20natural%20beige%20tones%2C%20desaturated%20calm%20lighting&width=600&height=400&seq=project-branding-work&orientation=landscape"
                    />
                  </div>
                  <div className="space-y-6 lg:order-1">
                    <h3 className="text-2xl md:text-3xl font-semibold text-[#4A4A48]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      「らしさ」を言葉にする
                    </h3>
                    <p className="text-base md:text-xl text-[#4A4A48] leading-[1.75]" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      企業の理念や採用メッセージを"共感される"形に。<br />
                      経営者インタビューからコピーライティングまで支援。
                    </p>
                    <div className="space-y-2 text-[#4A4A48] text-base md:text-xl" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      <p>・企業理念／採用文キャッチコピー作成</p>
                      <p>・代表メッセージ・コーポレートサイト文章設計</p>
                      <p>・トーン＆ボイス策定・社内共有資料整備</p>
                    </div>
                    <p className="text-base md:text-xl text-[#4A4A48] italic opacity-80" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                      想いの軸を、言葉で見える化する。
                    </p>
                  </div>
                </div>
              </div>
            </FadeUpAnimation>
          </section>
        </div>

        {/* ---------- CTA ---------- */}
        <section
          className="py-24 bg-[#4A4A48] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/e2213d932679368b2c76646fe04e2a17.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#4A4A48]/85"></div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <FadeUpAnimation delay={150} duration={1400}>
              <div className="text-center bg-[#4A4A48] p-16 rounded-xl">
                <h2 className="text-sm md:text-lg font-semibold text-white mb-6" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                  あなたの「想い」。<br />
                  それが、次のプロジェクト。
                </h2>
                <p className="text-white/90 leading-[1.8] mb-8 max-w-2xl mx-auto text-sm md:text-base" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                  形にしたい想いがあるならまずはお話してみませんか。<br />
                  まだ輪郭のないアイデアも、共に形にしていきましょう。
                </p>
                <a
                  href="mailto:tobe26.2b@gmail.com"
                  className="inline-flex items-center px-12 py-4 bg-white text-[#4A4A48] rounded-lg hover:bg-gray-50 transition-all duration-300 text-sm md:text-base font-medium whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                >
                  お問い合わせ
                </a>
              </div>
            </FadeUpAnimation>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
